import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type InvitationPageState =
  | 'loading'
  | 'invalid'
  | 'expired'
  | 'already_responded'
  | 'pending'
  | 'declined'
  | 'accepted'
  | 'error';

export interface PublicInvitationData {
  invitationId: string;
  companyName: string;
  companyPublicName: string;
  companyLogoUrl?: string;
  permission: string;
  jobRole: string | null;
  type: string;
  invitedByEmail: string;
  expiresAt: { _seconds: number } | string;
  status: string;
}

export interface UseInvitationReturn {
  pageState: InvitationPageState;
  invitationData: PublicInvitationData | null;
  isSubmitting: boolean;
  errorMessage: string | null;
  /** Called when user clicks "Rifiuta" — no auth needed */
  handleDecline: () => Promise<void>;
  /** Called when user clicks "Accetta" — checks auth, redirects to login if needed */
  handleAccept: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useInvitation — manages the full invite page state machine.
 *
 * States:
 *   loading           → fetching invitation data
 *   invalid           → token malformed or not found
 *   expired           → invitation TTL passed
 *   already_responded → invitation already accepted or declined
 *   pending           → awaiting user action (Accetta / Rifiuta)
 *   declined          → user clicked Rifiuta successfully
 *   accepted          → user accepted + /complete confirmed
 *   error             → unexpected server error
 *
 * Auth flow for acceptance:
 *   - If authenticated: call /complete directly with Bearer token
 *   - If not authenticated: redirect to /login?redirect=/i/:invitationId/:token
 *     After login/signup the user returns here and handleAccept runs again.
 *
 * Decline flow:
 *   - Always public, no auth required.
 */
export function useInvitation(invitationId: string, token: string): UseInvitationReturn {
  const [pageState, setPageState] = useState<InvitationPageState>('loading');
  const [invitationData, setInvitationData] = useState<PublicInvitationData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { firebaseUser, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Fetch invitation data — wait for auth to settle first to avoid race
  useEffect(() => {
    if (authLoading) return; // wait for Firebase auth state to resolve
    if (!invitationId || !token) {
      setPageState('invalid');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const data = await api.invitations.getPublic(invitationId, token);
        if (cancelled) return;

        setInvitationData(data);

        // Map backend status to page state
        if (data.status === 'accepted') {
          setPageState('accepted');
        } else if (data.status === 'declined') {
          setPageState('declined');
        } else if (data.status === 'expired' || data.status === 'superseded') {
          setPageState('expired');
        } else {
          setPageState('pending');
        }
      } catch (err) {
        if (cancelled) return;
        const error = err as { status?: number; message?: string };
        if (error.status === 404) {
          setPageState('invalid');
        } else if (error.status === 410) {
          setPageState('expired');
        } else if (error.status === 409) {
          setPageState('already_responded');
        } else {
          setErrorMessage(error.message ?? 'Errore nel caricamento dell\'invito.');
          setPageState('error');
        }
      }
    })();

    return () => { cancelled = true; };
  }, [invitationId, token, authLoading]);

  // Handle decline — no auth needed
  const handleDecline = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      await api.invitations.respond(invitationId, token, 'decline');
      setInvitationData((prev) => prev ? { ...prev, status: 'declined' } : prev);
      setPageState('declined');
    } catch (err) {
      const error = err as { message?: string };
      setErrorMessage(error.message ?? 'Errore durante il rifiuto dell\'invito.');
    } finally {
      setIsSubmitting(false);
    }
  }, [invitationId, token, isSubmitting]);

  // Handle accept
  const handleAccept = useCallback(async () => {
    if (isSubmitting) return;
    setErrorMessage(null);

    // Not authenticated — redirect to login, which will return here after auth
    if (!firebaseUser) {
      const redirectPath = `/i/${invitationId}/${token}`;
      navigate(`/login?redirect=${encodeURIComponent(redirectPath)}`);
      return;
    }

    // Authenticated — call /complete directly
    setIsSubmitting(true);
    try {
      const bearerToken = await firebaseUser.getIdToken();
      await api.invitations.complete(invitationId, token, bearerToken);
      setPageState('accepted');
    } catch (err) {
      const error = err as { status?: number; message?: string };
      if (error.status === 409) {
        try {
          const data = await api.invitations.getPublic(invitationId, token);
          setInvitationData(data);
          if (data.status === 'declined') {
            setPageState('declined');
            return;
          }
          if (data.status === 'accepted') {
            setPageState('accepted');
            return;
          }
          setPageState('already_responded');
          return;
        } catch {
          setPageState('already_responded');
          return;
        }
      }
      if (error.status === 403) {
        // Email mismatch — authenticated user is not the invited person
        setErrorMessage('Non puoi accettare questo invito con l\'account utilizzato. Accedi con l\'account corretto.');
      } else {
        setErrorMessage(error.message ?? 'Errore durante l\'accettazione dell\'invito.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [invitationId, token, isSubmitting, firebaseUser, navigate]);

  return {
    pageState,
    invitationData,
    isSubmitting,
    errorMessage,
    handleDecline,
    handleAccept,
  };
}
